import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { formatSyncErrorDetails } from '@core/sync/errorFormat'
import { useSupabaseSync } from '@/hooks/useSupabaseSync'
import type { SyncExecutionPlan, SyncPreflightReport, SyncResolutionAction, SyncTriggerSource } from '@/features/sync/types'
import { useI18n } from '@/i18n/useI18n'

type OpenDetail = { report?: SyncPreflightReport; trigger?: SyncTriggerSource }

function normalizeModalSyncError(error: unknown): string {
  const raw = formatSyncErrorDetails(error)
  if (/SESSION_EXPIRED|SESSION_UNAVAILABLE/i.test(raw)) return 'Necesitas volver a iniciar sesión.'
  if (/NOOP_REMOTE_UNVERIFIED/i.test(raw)) return 'No hay cambios locales; no se aplicó nada.'
  return raw
}

function defaultResolutionFor(r: SyncPreflightReport['risks'][number]): SyncResolutionAction {
  if (r.allowedResolutions?.length) {
    if (r.suggestedResolution && r.allowedResolutions.includes(r.suggestedResolution)) return r.suggestedResolution
    return r.allowedResolutions[0]
  }
  // Safety-first defaults: preserve both for HIGH risks; otherwise follow suggestion or keep_remote.
  if (r.suggestedResolution) return r.suggestedResolution
  if (r.level === 'HIGH') return 'preserve_both'
  return 'keep_remote'
}

function resolutionOptionsFor(risk: SyncPreflightReport['risks'][number]): SyncResolutionAction[] {
  return risk.allowedResolutions?.length
    ? risk.allowedResolutions
    : ['keep_local', 'keep_remote', 'preserve_both', 'abort']
}

export const SyncCenterModal: React.FC = () => {
  const { t } = useI18n()
  const { enabled, blockedByBeta, prepareSync, executeSafeSync } = useSupabaseSync()
  const [open, setOpen] = useState(false)
  const [report, setReport] = useState<SyncPreflightReport | undefined>(undefined)
  const [syncTrigger, setSyncTrigger] = useState<SyncTriggerSource>('manual_sync_safe')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDegradedPush, setConfirmDegradedPush] = useState(false)

  const risks = report?.risks ?? []
  const [resolutionsByRiskId, setResolutionsByRiskId] = useState<Record<string, SyncResolutionAction>>({})

  const initResolutions = useCallback((r?: SyncPreflightReport) => {
    const next: Record<string, SyncResolutionAction> = {}
    for (const risk of (r?.risks ?? [])) {
      if (!risk.requiresDecision) continue
      next[risk.id] = defaultResolutionFor(risk)
    }
    setResolutionsByRiskId(next)
  }, [])

  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<OpenDetail>).detail
      setOpen(true)
      setBusy(false)
      setError(null)
      setReport(detail?.report)
      setSyncTrigger(detail?.trigger ?? 'manual_sync_safe')
      setConfirmDegradedPush(false)
      initResolutions(detail?.report)
    }
    window.addEventListener('wrlens:sync-center-open', handler as any)
    return () => window.removeEventListener('wrlens:sync-center-open', handler as any)
  }, [initResolutions])

  useEffect(() => {
    if (!open) return
    if (report) return
    // If opened without a report, run a preflight on demand.
    ;(async () => {
      try {
        setBusy(true)
        const r = await prepareSync()
        if (r) {
          setReport(r)
          initResolutions(r)
        }
      } catch (e: any) {
        setError(normalizeModalSyncError(e))
      } finally {
        setBusy(false)
      }
    })()
  }, [open, report, prepareSync, initResolutions])

  const close = () => {
    setOpen(false)
    setBusy(false)
    setError(null)
  }

  const countsLine = useMemo(() => {
    if (!report) return ''
    const l = report.local
    const r = report.remote
    return t('sync.counts_line', {
      lp: l.projects,
      lv: l.writing_versions,
      ls: l.sections,
      lc: l.section_contents,
      rp: r.projects ?? '-',
      rv: r.writing_versions ?? '-',
      rs: r.sections ?? '-',
      rc: r.section_contents ?? '-',
    })
  }, [report, t])

  const requiredDecisionsMissing = useMemo(() => {
    if (!report) return false
    for (const risk of report.risks) {
      if (!risk.requiresDecision) continue
      if (!resolutionsByRiskId[risk.id]) return true
    }
    return false
  }, [report, resolutionsByRiskId])

  const isPullIncompleteError = useMemo(
    () => Boolean(error && /pull incompleto/i.test(error)),
    [error]
  )
  const isDegradedReport = Boolean(report?.backendHealth && report.backendHealth !== 'ok')
  const remoteUnverified = report?.remoteVerification === 'unverified'
  const degradedPushEligible = Boolean(remoteUnverified && report?.canDegradedLocalPush)
  const degradedNoChanges = Boolean(remoteUnverified && !report?.canDegradedLocalPush)
  const strictBlockedByPolicy = Boolean(remoteUnverified && degradedPushEligible)
  const degradedPullEligible = Boolean(remoteUnverified && !report?.canDegradedLocalPush)

  const retryAnalysis = async () => {
    if (!report) return
    setBusy(true)
    setError(null)
    try {
      const next = await prepareSync({
        mode: report.scopeMode ?? (report.scopeProjectIds?.length ? 'active_project' : 'all_projects'),
        projectIds: report.scopeProjectIds,
      })
      if (next) {
        setReport(next)
        setConfirmDegradedPush(false)
        initResolutions(next)
      }
    } catch (e: any) {
      setError(normalizeModalSyncError(e))
    } finally {
      setBusy(false)
    }
  }

  const run = async () => {
    if (!enabled) {
      setError(t('sync.error_enable_sync'))
      return
    }
    if (blockedByBeta) {
      setError(t('sync.error_beta_blocked'))
      return
    }
    if (strictBlockedByPolicy) {
      setError(t('sync.strict_blocked_unverified'))
      return
    }
    if (!report) return
    setBusy(true)
    setError(null)
    // Safety timeout: if executeSafeSync hangs beyond 90s, release the UI.
    const safety = setTimeout(() => {
      setBusy(false)
      setError(t('sync.error_timeout'))
    }, 90_000)
    try {
      const plan: SyncExecutionPlan = {
        createdAt: new Date().toISOString(),
        trigger: syncTrigger,
        strategy: degradedPullEligible ? 'pull_remote_only' : undefined,
        continueOnProjectUpsertFailure: false,
        scopeMode: report.scopeMode ?? (report.scopeProjectIds?.length ? 'active_project' : 'all_projects'),
        projectIds: report.scopeProjectIds,
        resolutionsByRiskId,
        createLocalSnapshot: degradedPullEligible ? false : true,
        createCloudSnapshot: false,
      }
      await executeSafeSync(plan, report)
      close()
    } catch (e: any) {
      setError(normalizeModalSyncError(e))
    } finally {
      clearTimeout(safety)
      setBusy(false)
    }
  }

  const runDegradedLocalPush = async () => {
    if (!enabled) {
      setError(t('sync.error_enable_sync'))
      return
    }
    if (blockedByBeta) {
      setError(t('sync.error_beta_blocked'))
      return
    }
    if (!report) return
    if (!degradedPushEligible) {
      setError(t('sync.degraded_push_no_changes'))
      return
    }
    if (!confirmDegradedPush) {
      setError(t('sync.degraded_push_confirm_required'))
      return
    }

    setBusy(true)
    setError(null)
    const safety = setTimeout(() => {
      setBusy(false)
      setError(t('sync.error_timeout'))
    }, 90_000)
    try {
      const plan: SyncExecutionPlan = {
        createdAt: new Date().toISOString(),
        trigger: syncTrigger,
        strategy: 'push_local_only',
        continueOnProjectUpsertFailure: true,
        scopeMode: report.scopeMode ?? (report.scopeProjectIds?.length ? 'active_project' : 'all_projects'),
        projectIds: report.scopeProjectIds,
        resolutionsByRiskId: {},
        createLocalSnapshot: true,
        createCloudSnapshot: false,
      }
      await executeSafeSync(plan, report)
      close()
    } catch (e: any) {
      setError(normalizeModalSyncError(e))
    } finally {
      clearTimeout(safety)
      setBusy(false)
    }
  }

  // Bulk resolution helpers
  const setAllResolutions = useCallback((action: SyncResolutionAction) => {
    const next: Record<string, SyncResolutionAction> = {}
    for (const risk of (report?.risks ?? [])) {
      if (!risk.requiresDecision) continue
       if (!resolutionOptionsFor(risk).includes(action)) continue
      next[risk.id] = action
    }
    setResolutionsByRiskId(next)
  }, [report])

  const requiresDecisionCount = useMemo(() =>
    (report?.risks ?? []).filter(r => r.requiresDecision).length
  , [report])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4">
      {/* Modal: flex column con altura máxima para que sea scrollable en móvil */}
      <div className="w-full max-w-3xl rounded-2xl border border-white/15 bg-[#151515] text-[#F8F6EF] shadow-2xl flex flex-col max-h-[95dvh]">

        {/* ── Cabecera fija ── */}
        <div className="flex items-start justify-between gap-3 p-4 sm:p-6 border-b border-white/10 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-2xl font-semibold">{t('sync.title')}</h2>
            <p className="text-xs text-[#ebd4b5] mt-1">
              {t('sync.subtitle')}
            </p>
            {report ? <p className="text-[10px] text-gray-500 mt-1 truncate">{countsLine}</p> : null}
          </div>
          <button
            type="button"
            className="shrink-0 rounded-md border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10"
            onClick={close}
          >
            {busy ? t('sync.close_force') : t('common.close')}
          </button>
        </div>

        {/* ── Botones de resolución masiva (solo cuando hay conflictos) ── */}
        {requiresDecisionCount > 0 && !busy ? (
          <div className="flex items-center gap-2 px-4 sm:px-6 py-3 border-b border-white/10 shrink-0 bg-black/20">
            <span className="text-xs text-gray-400 mr-1">{t('sync.conflicts_apply_all', { count: requiresDecisionCount, suffix: requiresDecisionCount > 1 ? 's' : '' })}</span>
            <button
              type="button"
              onClick={() => setAllResolutions('keep_local')}
              className="rounded-lg bg-blue-700 hover:bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              ✓ {t('sync.apply_local')}
            </button>
            <button
              type="button"
              onClick={() => setAllResolutions('keep_remote')}
              className="rounded-lg border border-white/20 hover:bg-white/10 px-3 py-1.5 text-xs text-gray-300"
            >
              {t('sync.apply_remote')}
            </button>
          </div>
        ) : null}

        {/* ── Cuerpo scrollable ── */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
          {isDegradedReport ? (
            <div className="rounded-xl border border-amber-500/40 bg-amber-900/20 px-4 py-3 text-sm text-amber-100">
              {report?.remoteCountsTimedOut
                ? t('sync.degraded_timeout')
                : t('sync.degraded_error', { error: report?.remoteCountsError || t('sync.degraded_generic') })}
            </div>
          ) : null}

          {remoteUnverified ? (
            <div className="rounded-xl border border-amber-400/30 bg-amber-950/30 px-4 py-3 text-sm text-amber-100 space-y-2">
              <div className="font-medium">{t('sync.degraded_unverified_title')}</div>
              <div className="text-amber-200">
                {t('sync.degraded_unverified_desc', {
                  versions: report?.localDirty?.versions ?? 0,
                  sections: report?.localDirty?.sections ?? 0,
                  contents: report?.localDirty?.contents ?? 0,
                })}
              </div>
              {degradedPushEligible ? (
                <label className="flex items-start gap-2 text-xs text-amber-200">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={confirmDegradedPush}
                    onChange={(e) => setConfirmDegradedPush(e.target.checked)}
                    disabled={busy}
                  />
                  <span>{t('sync.degraded_push_confirm')}</span>
                </label>
              ) : null}
              {degradedNoChanges ? (
                <div className="text-xs text-amber-200">No hay cambios locales pendientes. Puedes intentar traer cambios remotos igualmente.</div>
              ) : null}
              {strictBlockedByPolicy ? (
                <div className="text-xs text-amber-100">{t('sync.strict_blocked_unverified')}</div>
              ) : null}
              <div>
                <button
                  type="button"
                  className="rounded-md border border-amber-300/40 px-2.5 py-1 text-xs text-amber-100 hover:bg-amber-900/30 disabled:opacity-50"
                  onClick={retryAnalysis}
                  disabled={busy}
                >
                  {t('sync.retry_analysis')}
                </button>
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-xl border border-red-500/40 bg-red-900/20 px-4 py-3 text-sm text-red-200">
              <div>{error}</div>
              {isPullIncompleteError ? (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={run}
                    disabled={busy}
                    className="rounded-md border border-red-200/40 px-3 py-1.5 text-xs text-red-100 hover:bg-red-800/30 disabled:opacity-60"
                  >
                    {t('sync.retry')}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {busy && !report ? <div className="text-sm text-gray-300">{t('sync.preparing')}</div> : null}
          {busy && report ? <div className="text-sm text-gray-400 animate-pulse">{t('sync.running')}</div> : null}

          {report && risks.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-gray-300">
              {strictBlockedByPolicy ? t('sync.no_conflicts_degraded') : t('sync.no_conflicts')}
            </div>
          ) : null}

          {report && risks.length > 0 ? risks.map((r) => (
            <div key={r.id} className="rounded-xl border border-white/10 bg-black/10 p-4">
              <div className="flex flex-col gap-3">
                {/* Info del conflicto */}
                <div>
                  <div className="text-sm font-semibold flex flex-wrap items-center gap-1">
                    <span className={r.level === 'HIGH' ? 'text-red-300' : r.level === 'MEDIUM' ? 'text-amber-200' : 'text-gray-200'}>
                      {r.level}
                    </span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-200" title={r.entityId}>
                      {r.label ?? r.entityType}
                    </span>
                  </div>
                  <div className="text-xs text-[#ebd4b5] mt-1">{r.reason}</div>
                </div>

                {/* Select de resolución — full width, bien visible en móvil */}
                {r.requiresDecision ? (
                  <div>
                    <label className="block text-[11px] text-gray-400 mb-1">{t('sync.resolution')}</label>
                    {(() => {
                      const options = resolutionOptionsFor(r)
                      return (
                    <select
                      className="w-full rounded-lg border border-white/20 bg-[#1a1a1a] px-3 py-2 text-sm text-[#F8F6EF]"
                      value={resolutionsByRiskId[r.id] || ''}
                      onChange={(e) => setResolutionsByRiskId((prev) => ({ ...prev, [r.id]: e.target.value as SyncResolutionAction }))}
                      disabled={busy}
                    >
                      {options.includes('keep_local') ? <option value="keep_local">✓ {t('sync.option_keep_local')}</option> : null}
                      {options.includes('keep_remote') ? <option value="keep_remote">{t('sync.option_keep_remote')}</option> : null}
                      {options.includes('preserve_both') ? <option value="preserve_both">{t('sync.option_preserve_both')}</option> : null}
                      {options.includes('abort') ? <option value="abort">{t('sync.option_abort')}</option> : null}
                    </select>
                      )
                    })()}
                    {resolutionsByRiskId[r.id] === 'keep_remote' ? (
                      <div className="text-[11px] text-amber-200 mt-1">
                        {t('sync.warning_overwrite_local')}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="text-[11px] text-gray-500">{t('sync.no_decision_needed')}</div>
                )}
              </div>
            </div>
          )) : null}
        </div>

        {/* ── Pie fijo con botones de acción ── */}
        <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-white/10 shrink-0">
          {strictBlockedByPolicy ? (
            <div className="mr-auto text-xs text-amber-200">{t('sync.strict_blocked_unverified')}</div>
          ) : null}
          {degradedPushEligible ? (
            <button
              type="button"
              className="rounded-lg border border-amber-300/50 px-4 py-2 text-sm text-amber-100 hover:bg-amber-900/30 disabled:opacity-50"
              onClick={runDegradedLocalPush}
              disabled={busy || !confirmDegradedPush}
            >
              {t('sync.degraded_push_action')}
            </button>
          ) : null}
          <button
            type="button"
            className="rounded-lg border border-white/20 px-4 py-2 text-sm hover:bg-white/10"
            onClick={close}
          >
            {t('common.cancel')}
          </button>
          {!strictBlockedByPolicy ? (
            <button
              type="button"
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
              onClick={run}
              disabled={busy || !report || requiredDecisionsMissing}
              title={requiredDecisionsMissing ? t('sync.choose_resolution') : undefined}
            >
              {busy ? t('usermenu.sync_syncing') : degradedPullEligible ? 'Intentar bajar remoto' : t('sync.run')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
